/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FlexContainer, Text, Spinner, Icon } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import EventService from 'Services/events';
import { hideModalAction, showModalAction } from 'Store/modules/app/actions';
import { pathNameSelector } from 'Store/modules/router/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { useFlags } from 'Hooks/useFlags';
import { useFetchCheckout, useFetchGetCustomers } from 'Containers/Plans/hooks';
import { BodyObject } from 'Containers/Plans/components/CardPlan/types';
import { addNewParams } from 'Containers/Plans/utils';
import { CloseIcon } from 'Components/icons';
import OxxoPayment from 'Components/icons/OxxoPayment';
import { removeOverflow, setBodyOverflow } from 'Styles/utils/utils';
import { routes } from 'Constants/routes';
import { RESULT_MANDATORY_MODAL } from 'Constants/modals';
import { FlagsKey } from 'Types/common';
import * as Styled from './styles';
import { PaymentProps } from './types';

const PaymentMethod = ({ data: props }: PaymentProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.MODALS);
  const [customerId, setCustomerId] = useState<string>('');
  const { data: customer } = useFetchGetCustomers(true);
  const path = useSelector(pathNameSelector);
  const { enabled } = useFlags(FlagsKey.RESULT_MANDATORY_UPSELLING);
  const { mutate, isLoading } = useFetchCheckout();
  const dispatch = useDispatch();
  const { successUrl, cancelUrl, upsellingProps } = props;
  const { id, price } = upsellingProps;

  const handleClickPayment = (method: string): void => {
    const body: BodyObject = {
      productId: id,
      checkoutConfig: {
        successUrl: addNewParams(successUrl, { method }),
        cancelUrl,
        paymentMethod: method,
      },
    };
    EventService.orderStarted(path, price.id);
    mutate(body);
  };

  useEffect(() => {
    setBodyOverflow();
  }, []);

  const mandatoryResultModalOpen = (): void => {
    if (window.location.pathname === routes.RESULT && enabled && !props.isPayLater) {
      dispatch(showModalAction({ options: { type: RESULT_MANDATORY_MODAL } }));
    }
  };
  const closeModal = (): void => {
    removeOverflow();
    dispatch(hideModalAction());
    mandatoryResultModalOpen();
  };

  useEffect(() => {
    if (customer?.id) {
      setCustomerId(customer.id);
    }
  }, [customer]);

  return (
    <Styled.ModalContainer>
      <Styled.ModalContent>
        <Styled.CloseButton onClick={closeModal}>
          <CloseIcon />
        </Styled.CloseButton>
        <Text
          tag="h1"
          align="center"
          type="fds_paragraph_semibold_L"
          mb="fds_size_08"
          color="green_600"
        >
          {t('PaymentMethod.title')}
        </Text>
        <Text type="fds_paragraph_medium_M" bold align="center">
          {t('PaymentMethod.subtitle')}
        </Text>
        {isLoading || !customerId ? (
          <Styled.SpinnerContainer justify="center" align="center">
            <Spinner size="M" />
          </Styled.SpinnerContainer>
        ) : (
          <FlexContainer justify="space-around" direction="column" mt="fds_size_08">
            <Styled.MethodContainer
              align="center"
              justify="center"
              className="PayPalButtons"
              type="card"
              onClick={() => handleClickPayment('card')}
            >
              <Styled.IconContainer align="center" justify="center" gap="dimension_04">
                <Icon icon="IconPadLock" color="light" />
                <Text type="paragraphs" color="light" size="S_XL_S">
                  {t('PaymentMethod.creditCard')}
                </Text>
              </Styled.IconContainer>
            </Styled.MethodContainer>
            <Styled.MethodContainer
              type="oxxo"
              className="PayPalButtons"
              onClick={() => handleClickPayment('oxxo')}
              align="center"
              justify="center"
            >
              <OxxoPayment />
              <Text color="light" type="paragraphs" size="S_XL_S" ml="fds_size_08">
                {t('Payment.cash.title')}
              </Text>
            </Styled.MethodContainer>
          </FlexContainer>
        )}
      </Styled.ModalContent>
    </Styled.ModalContainer>
  );
};

export default PaymentMethod;
